import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  MessageSquare, Send, Loader2, ArrowLeft, Plus, User, Clock, Circle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MessagesPage() {
  const { user, api } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msgLoading, setMsgLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [instructors, setInstructors] = useState([]);
  const [learners, setLearners] = useState([]);
  const [newConvo, setNewConvo] = useState({ instructor_id: '', learner_id: '', subject: '', message: '' });

  const isParent = user?.role === 'parent';

  const fetchConversations = useCallback(async () => {
    try {
      const res = await api.get('/conversations/');
      setConversations(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Error fetching conversations:', e);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  useEffect(() => {
    if (showNewDialog && isParent) {
      fetch(`${API_URL}/api/courses/instructors/`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` } })
        .then(r => r.json()).then(d => setInstructors(Array.isArray(d) ? d : d.results || []))
        .catch(() => {
          fetch(`${API_URL}/api/admin/stem-certificates/learners/`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` } })
            .then(r => r.json()).then(() => {}).catch(() => {});
        });
      api.get('/learners/').then(r => setLearners(r.data.results || r.data || [])).catch(() => {});
    }
  }, [showNewDialog, isParent, api]);

  const openConversation = async (convoId) => {
    setMsgLoading(true);
    try {
      const res = await api.get(`/conversations/${convoId}/`);
      setSelectedConvo(res.data.conversation);
      setMessages(res.data.messages || []);
      fetchConversations();
    } catch (e) {
      toast.error('Failed to load conversation');
    } finally {
      setMsgLoading(false);
    }
  };

  const handleReply = async () => {
    if (!reply.trim() || !selectedConvo) return;
    setSending(true);
    try {
      const res = await api.post(`/conversations/${selectedConvo.id}/reply/`, { content: reply });
      setMessages(prev => [...prev, res.data]);
      setReply('');
      fetchConversations();
    } catch (e) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleNewConversation = async () => {
    if (!newConvo.message.trim()) { toast.error('Write a message'); return; }
    if (!newConvo.instructor_id) {
      toast.error(isParent ? 'Please select an instructor to message' : 'Please select a parent to message');
      return;
    }
    setSending(true);
    try {
      const payload = {
        subject: newConvo.subject || 'New Message',
        message: newConvo.message,
      };
      if (isParent) {
        payload.instructor_id = newConvo.instructor_id;
        payload.learner_id = newConvo.learner_id || undefined;
      } else {
        payload.parent_id = newConvo.instructor_id;
      }
      const res = await api.post('/conversations/', payload);
      setShowNewDialog(false);
      setNewConvo({ instructor_id: '', learner_id: '', subject: '', message: '' });
      toast.success('Message sent!');
      fetchConversations();
      openConversation(res.data.id);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="messages-loading">
        <Loader2 className="h-8 w-8 animate-spin text-rsc-navy" />
      </div>
    );
  }

  // Conversation detail view
  if (selectedConvo) {
    return (
      <div className="space-y-4" data-testid="conversation-detail">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setSelectedConvo(null)} data-testid="back-to-inbox">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div>
            <h2 className="font-semibold text-gray-900">{selectedConvo.subject || 'Conversation'}</h2>
            <p className="text-xs text-gray-500">
              With {isParent ? selectedConvo.instructor_name : selectedConvo.parent_name}
              {selectedConvo.learner_name && ` — Re: ${selectedConvo.learner_name}`}
            </p>
          </div>
        </div>

        <Card>
          <CardContent className="p-4">
            {msgLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
            ) : (
              <div className="space-y-4 max-h-[400px] overflow-y-auto mb-4" data-testid="messages-list">
                {messages.map(msg => {
                  const isMe = msg.sender_id === user?.id;
                  return (
                    <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                        isMe ? 'bg-rsc-navy text-white rounded-br-md' : 'bg-gray-100 text-gray-900 rounded-bl-md'
                      }`} data-testid={`message-${msg.id}`}>
                        <p className="text-sm">{msg.content}</p>
                        <p className={`text-[10px] mt-1 ${isMe ? 'text-white/50' : 'text-gray-400'}`}>
                          {msg.sender_name} — {new Date(msg.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  );
                })}
                {messages.length === 0 && <p className="text-center text-gray-400 text-sm py-8">No messages yet. Start the conversation!</p>}
              </div>
            )}
            <div className="flex gap-2 border-t pt-3">
              <Input
                placeholder="Type a message..."
                value={reply}
                onChange={e => setReply(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleReply()}
                data-testid="reply-input"
              />
              <Button onClick={handleReply} disabled={sending || !reply.trim()} className="bg-rsc-navy" data-testid="send-reply">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Inbox view
  return (
    <div className="space-y-6" data-testid="messages-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MessageSquare className="h-6 w-6 text-rsc-navy" /> Messages
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {isParent ? 'Communicate with your child\'s instructors' : 'Messages from parents'}
          </p>
        </div>
        <Button onClick={() => setShowNewDialog(true)} className="bg-rsc-navy" data-testid="new-message-btn">
          <Plus className="h-4 w-4 mr-2" /> New Message
        </Button>
      </div>

      {conversations.length === 0 ? (
        <Card className="border-dashed border-2" data-testid="no-messages">
          <CardContent className="p-12 text-center">
            <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="font-semibold text-gray-700 mb-2">No Messages Yet</h3>
            <p className="text-sm text-gray-500 mb-4">
              {isParent
                ? 'Start a conversation with your child\'s instructor to ask questions or share updates.'
                : 'No parent messages yet. Messages will appear here when parents reach out.'}
            </p>
            <Button onClick={() => setShowNewDialog(true)} variant="outline" data-testid="start-convo-btn">
              <Plus className="h-4 w-4 mr-2" /> Start a Conversation
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="conversations-list">
          {conversations.map(c => {
            const isUnread = isParent ? !c.is_read_by_parent : !c.is_read_by_instructor;
            return (
              <Card
                key={c.id}
                className={`cursor-pointer hover:shadow-md transition-shadow ${isUnread ? 'border-rsc-navy/30 bg-blue-50/30' : ''}`}
                onClick={() => openConversation(c.id)}
                data-testid={`convo-${c.id}`}
              >
                <CardContent className="p-4 flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isUnread ? 'bg-rsc-navy text-white' : 'bg-gray-100 text-gray-500'}`}>
                    <User className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`text-sm truncate ${isUnread ? 'font-bold text-gray-900' : 'font-medium text-gray-700'}`}>
                        {isParent ? c.instructor_name : c.parent_name}
                      </p>
                      {isUnread && <Circle className="h-2 w-2 fill-rsc-navy text-rsc-navy flex-shrink-0" />}
                      {c.learner_name && <Badge variant="outline" className="text-[10px] px-1.5 py-0">{c.learner_name}</Badge>}
                    </div>
                    <p className={`text-xs truncate ${isUnread ? 'text-gray-700' : 'text-gray-500'}`}>
                      {c.subject || 'No subject'}
                      {c.last_message && ` — ${c.last_message.content}`}
                    </p>
                  </div>
                  <div className="text-xs text-gray-400 flex-shrink-0 flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {new Date(c.updated_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* New Conversation Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-rsc-navy" />
              New Message
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {isParent && (
              <div>
                <Label>To *</Label>
                <Select value={newConvo.instructor_id} onValueChange={v => setNewConvo(p => ({ ...p, instructor_id: v }))}>
                  <SelectTrigger data-testid="new-msg-instructor">
                    <SelectValue placeholder={instructors.length ? 'Select an instructor' : 'No instructors available'} />
                  </SelectTrigger>
                  <SelectContent>
                    {instructors.map(i => (
                      <SelectItem key={i.id} value={i.id}>
                        {i.display_name || `${i.first_name || ''} ${i.last_name || ''}`.trim() || i.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {isParent && learners.length > 0 && (
              <div>
                <Label>Regarding (optional)</Label>
                <Select value={newConvo.learner_id} onValueChange={v => setNewConvo(p => ({ ...p, learner_id: v }))}>
                  <SelectTrigger data-testid="new-msg-learner"><SelectValue placeholder="Select child (optional)" /></SelectTrigger>
                  <SelectContent>
                    {learners.map(l => <SelectItem key={l.id} value={l.id}>{l.first_name} {l.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Subject</Label>
              <Input placeholder="e.g., Question about homework" value={newConvo.subject}
                onChange={e => setNewConvo(p => ({ ...p, subject: e.target.value }))} data-testid="new-msg-subject" />
            </div>
            <div>
              <Label>Message *</Label>
              <Textarea placeholder="Type your message..." rows={4} value={newConvo.message}
                onChange={e => setNewConvo(p => ({ ...p, message: e.target.value }))} data-testid="new-msg-content" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDialog(false)}>Cancel</Button>
            <Button onClick={handleNewConversation} disabled={sending} className="bg-rsc-navy" data-testid="send-new-msg">
              {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Send Message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
